// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module ConvexAnalyticsStore
 * @package @geenius/analytics/convex
 * @description AnalyticsStorageAdapter wrapper for Convex clients. The factory
 * lets the shared tracker persist sanitized events through packaged Convex
 * mutations while preserving the same read-model contract as other DB backends.
 */

import {
  buildAnalyticsDashboardData,
  type AnalyticsDashboardData,
  type AnalyticsDashboardQuery,
  type AnalyticsEvent,
  type AnalyticsEventQuery,
  type AnalyticsPageViewQuery,
  type AnalyticsReadResult,
  type AnalyticsStorageAdapter,
  type ConvexAnalyticsEvent,
  type PageView,
  type RealtimeStats,
  type RealtimeStatsQuery,
  type UserTraits,
} from "@geenius/analytics-shared";
import { api } from "./_generated/api.js";

type JsonRecord = Record<string, string | number | boolean | null>;

/**
 * Minimal Convex client contract required by the analytics storage adapter.
 *
 * @property mutation Invokes a packaged Convex mutation reference with arguments.
 * @property query Invokes a packaged Convex query reference with arguments.
 */
export interface ConvexAnalyticsClient {
  mutation(
    functionReference: unknown,
    args: Record<string, unknown>,
  ): Promise<unknown>;
  query<TResult = unknown>(
    functionReference: unknown,
    args: Record<string, unknown>,
  ): Promise<TResult>;
}

/**
 * Options for creating a Convex-backed analytics storage adapter.
 *
 * @property client Convex client bound to the consumer application deployment.
 */
export interface ConvexAnalyticsStoreOptions {
  client: ConvexAnalyticsClient;
}

function toJsonRecord(
  value: Record<string, unknown> | undefined,
): JsonRecord | undefined {
  if (!value) return undefined;

  const record: JsonRecord = {};
  for (const [key, entry] of Object.entries(value)) {
    if (
      entry === null ||
      typeof entry === "string" ||
      typeof entry === "number" ||
      typeof entry === "boolean"
    ) {
      record[key] = entry;
    }
  }
  return record;
}

function matchesTimeRange(
  timestamp: number,
  timeRange: { start: number; end: number } | undefined,
): boolean {
  if (!timeRange) return true;
  return timestamp >= timeRange.start && timestamp <= timeRange.end;
}

function paginate<T>(
  items: T[],
  limit: number | undefined,
  cursor: string | undefined,
): AnalyticsReadResult<T> {
  const offset = cursor ? Number.parseInt(cursor, 10) : 0;
  const safeOffset = Number.isFinite(offset) && offset > 0 ? offset : 0;
  const safeLimit =
    typeof limit === "number" && limit > 0 ? Math.floor(limit) : items.length;
  const page = items.slice(safeOffset, safeOffset + safeLimit);
  const nextOffset = safeOffset + page.length;
  return {
    items: page,
    nextCursor: nextOffset < items.length ? String(nextOffset) : undefined,
    total: items.length,
  };
}

function matchesEventQuery(
  event: AnalyticsEvent,
  query: AnalyticsEventQuery = {},
): boolean {
  if (query.userId && event.userId !== query.userId) return false;
  if (query.sessionId && event.sessionId !== query.sessionId) return false;
  if (query.eventName && event.name !== query.eventName) return false;
  if (query.eventType && event.type !== query.eventType) return false;
  return matchesTimeRange(event.timestamp, query.timeRange);
}

function matchesPageViewQuery(
  pageView: PageView,
  query: AnalyticsPageViewQuery = {},
): boolean {
  if (query.url && pageView.url !== query.url) return false;
  if (query.userId && pageView.userId !== query.userId) return false;
  if (query.sessionId && pageView.sessionId !== query.sessionId) return false;
  return matchesTimeRange(pageView.timestamp, query.timeRange);
}

function toConvexDashboardEvent(event: AnalyticsEvent): ConvexAnalyticsEvent {
  return {
    ...event,
    event: event.name,
  };
}

function eventQueryArgs(query: AnalyticsEventQuery = {}): Record<string, unknown> {
  return {
    startTime: query.timeRange?.start,
    endTime: query.timeRange?.end,
    userId: query.userId,
    limit: 1000,
  };
}

function pageViewQueryArgs(
  query: AnalyticsPageViewQuery = {},
): Record<string, unknown> {
  return {
    startTime: query.timeRange?.start,
    endTime: query.timeRange?.end,
    limit: 1000,
  };
}

/**
 * Creates a Convex-backed storage adapter for the shared analytics tracker.
 *
 * Use this when an application already has a Convex client and wants
 * `createTracker({ storage })` to persist through the packaged Convex
 * mutations and queries instead of hand-written glue code.
 *
 * @param options Convex client options for the target deployment.
 * @returns An AnalyticsStorageAdapter backed by packaged Convex functions.
 *
 * @example
 * ```ts
 * const storage = createConvexAnalyticsStore({ client: convex })
 * const tracker = createTracker({ storage })
 * ```
 */
export function createConvexAnalyticsStore(
  options: ConvexAnalyticsStoreOptions,
): AnalyticsStorageAdapter {
  const { client } = options;
  const store: AnalyticsStorageAdapter = {
    async trackEvent(event) {
      return await client.mutation(api.mutations.trackEvent, {
        ...event,
        properties: toJsonRecord(event.properties),
        traits: toJsonRecord(event.traits),
      });
    },
    async trackPageView(pageView) {
      return await client.mutation(api.mutations.trackPageView, {
        ...pageView,
        properties: toJsonRecord(pageView.properties),
      });
    },
    async identifyUser(userId, traits) {
      return await client.mutation(api.mutations.identifyUser, {
        userId,
        traits: toJsonRecord(traits) ?? {},
      });
    },
    async setUserProperties(userId: string, traits: UserTraits) {
      return await client.mutation(api.mutations.setUserProperties, {
        userId,
        traits: toJsonRecord(traits) ?? {},
      });
    },
    async getEvents(query = {}) {
      const rows = await client.query<AnalyticsEvent[]>(
        api.queries.getEvents,
        eventQueryArgs(query),
      );
      const filtered = rows
        .filter((event) => matchesEventQuery(event, query))
        .sort((left, right) => left.timestamp - right.timestamp);
      return paginate(filtered, query.limit, query.cursor);
    },
    async getPageViews(query = {}) {
      const rows = await client.query<PageView[]>(
        api.queries.getPageViews,
        pageViewQueryArgs(query),
      );
      const filtered = rows
        .filter((pageView) => matchesPageViewQuery(pageView, query))
        .sort((left, right) => left.timestamp - right.timestamp);
      return paginate(filtered, query.limit, query.cursor);
    },
    async getDashboardData(query: AnalyticsDashboardQuery = {}) {
      const pageViews = (await store.getPageViews?.({
        timeRange: query.timeRange,
      }))?.items;
      const events = (await store.getEvents?.({ timeRange: query.timeRange }))
        ?.items;
      const data = buildAnalyticsDashboardData({
        pageViews: pageViews ?? [],
        events: (events ?? []).map(toConvexDashboardEvent),
      });
      return {
        ...data,
        topPages: data.topPages.slice(0, query.limit ?? data.topPages.length),
      } satisfies AnalyticsDashboardData;
    },
    async getRealtimeStats(query: RealtimeStatsQuery = {}) {
      const stats = await client.query<RealtimeStats>(
        api.queries.getRealtimeStats,
        {},
      );
      return {
        ...stats,
        lastUpdated: query.now ?? stats.lastUpdated,
      };
    },
    async flush() {
      return undefined;
    },
    async shutdown() {
      return undefined;
    },
    async purgeExpiredData(cutoffTimestamp) {
      return await client.mutation(api.mutations.purgeExpiredData, {
        cutoffTimestamp,
      });
    },
    async deleteUserData(userId) {
      return await client.mutation(api.mutations.deleteUserData, { userId });
    },
  };

  return store;
}
